import React, { useState, useEffect, useRef } from 'react';
import { MapPin, CheckCircle, NavigationArrow } from '@phosphor-icons/react';
import { toast } from 'sonner';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let scriptLoadingPromise = null;

function loadGoogleMapsScript() {
  if (window.google?.maps) return Promise.resolve();
  if (scriptLoadingPromise) return scriptLoadingPromise;

  scriptLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
    // Some browser extensions/ad-blockers silently strip the script tag
    // or block the request without ever firing onload/onerror, which
    // would otherwise hang this promise — and everything waiting on it —
    // forever. A hard timeout guarantees we always move on.
    setTimeout(() => reject(new Error('Google Maps script load timed out')), 8000);
  });
  return scriptLoadingPromise;
}

// Geocodes address text to a lat/lng via Google's Geocoding API. Shared by
// both the "does the customer's GPS tally with this address" check and the
// map's initial pin placement, so both always agree on where the address
// actually is.
async function geocodeAddress(addressText) {
  if (!addressText || !addressText.trim()) return null;
  await loadGoogleMapsScript();
  const geocoder = new window.google.maps.Geocoder();
  const geocodePromise = new Promise((resolve) => {
    geocoder.geocode({ address: addressText, componentRestrictions: { country: 'in' } }, (results, status) => {
      resolve(status === 'OK' && results?.[0] ? results[0].geometry.location : null);
    });
  });
  // The geocode callback can occasionally never fire (blocked request,
  // extension interference, transient network issue) which would hang
  // the whole location flow forever with no error shown. A timeout
  // guarantees we always fall through to the manual map pin instead.
  const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 6000));
  return Promise.race([geocodePromise, timeoutPromise]);
}

// Great-circle distance in meters — used to compare the customer's live
// GPS reading against the geocoded address point.
function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// The maximum leeway ever given, regardless of how (un)confident the
// phone's own GPS reading is — beyond this, cell-tower-only fallback in a
// low-signal area can be off by kilometers, so we simply stop trusting
// GPS as a check at all past this point and fall back to the map instead.
const MAX_TOLERANCE_M = 600;
// Even a very confident GPS reading shouldn't require an implausibly
// tight match — Google's own address pin has its own inherent imprecision
// (apartment complexes, new construction, etc.), independent of the
// phone's GPS quality.
const MIN_TOLERANCE_M = 75;

/**
 * Resolves an accurate lat/lng for a delivery address.
 *
 * The delivery address's GEOCODED location is always the source of truth
 * for pricing and rider navigation — never the customer's live GPS
 * directly. Where someone happens to be standing says nothing about
 * where their order needs to go; they might be running errands, ordering
 * ahead, or ordering for someone else entirely.
 *
 * GPS is used only as an optional trust signal, when the customer
 * confirms they're physically at the address right now: if their real
 * position reasonably tallies with where Google geocoded the address
 * text, that's a useful double-check that the address is correct — the
 * confirmed point is still the geocoded address, not the raw GPS fix.
 * The leeway allowed scales with how confident the phone's own reading
 * is (a tight match required for a precise fix, more room for a fuzzy
 * one), capped at MAX_TOLERANCE_M and floored at MIN_TOLERANCE_M.
 *
 * A mismatch — or no usable GPS at all (denied, switched off, poor
 * signal) — never blocks the order. It only ever falls through to the
 * same manual map pin-drop, centered on the geocoded address, that every
 * customer can already use regardless of GPS.
 */
export default function AddressLocationPicker({ onConfirm, onSkip, addressText }) {
  const [step, setStep] = useState('ask'); // 'ask' | 'checking' | 'map'
  const [mapLoading, setMapLoading] = useState(true);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [pinPosition, setPinPosition] = useState({ lat: 19.0760, lng: 72.8777 }); // Mumbai default center, only used until geocoding resolves

  const handleYes = async () => {
    if (!navigator.geolocation) {
      // No geolocation capability at all on this device/browser — nothing
      // to check against, so just move on to manual confirmation.
      setStep('map');
      return;
    }

    setStep('checking');

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;

        const addressPoint = await geocodeAddress(addressText).catch(() => null);
        if (!addressPoint) {
          // Couldn't geocode the typed address at all — fall back to the
          // map, where the customer can place the pin manually.
          setStep('map');
          return;
        }

        // Dynamic tolerance: 2x the phone's own reported accuracy radius
        // (a reasonable safety margin around a 1-standard-deviation
        // figure), clamped between the floor and ceiling defined above.
        const tolerance = Math.min(MAX_TOLERANCE_M, Math.max(MIN_TOLERANCE_M, accuracy * 2));

        const distance = distanceMeters(latitude, longitude, addressPoint.lat(), addressPoint.lng());

        if (distance <= tolerance) {
          // Tallies — the geocoded address point is confirmed accurate.
          // Note: we confirm the GEOCODED point, not the raw GPS fix.
          toast.success('Location confirmed — matches your current position');
          onConfirm(addressPoint.lat(), addressPoint.lng());
        } else {
          // Doesn't tally. Never block the order over this — just ask
          // for a manual check instead, framed gently since it may well
          // be Google's geocoding that's slightly off, not the customer.
          toast.info("That's a bit further from this address than expected — please confirm the exact spot on the map");
          setStep('map');
        }
      },
      () => {
        // Permission denied, location switched off, or a genuine GPS
        // error — none of these are the customer's fault in any way we
        // can hold against them. Quietly fall back to the map.
        setStep('map');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleNo = () => setStep('map');

  // Load Google Maps once, then initialize a real Google Map with a
  // draggable pin — centered via Google's own Geocoding API on the address
  // text already typed, so the customer starts near the real destination
  // instead of having to drag across the whole map.
  useEffect(() => {
    if (step !== 'map') return;

    let cancelled = false;
    setMapLoading(true);

    const initMap = async () => {
      if (cancelled || !mapContainerRef.current) return;
      const google = window.google;

      let center = pinPosition;
      const result = await geocodeAddress(addressText).catch(() => null);
      if (result) center = { lat: result.lat(), lng: result.lng() };
      if (cancelled) return;

      const map = new google.maps.Map(mapContainerRef.current, {
        center,
        zoom: 16,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      });
      const marker = new google.maps.Marker({
        position: center,
        map,
        draggable: true,
      });
      marker.addListener('dragend', () => {
        const pos = marker.getPosition();
        setPinPosition({ lat: pos.lat(), lng: pos.lng() });
      });
      map.addListener('click', (e) => {
        marker.setPosition(e.latLng);
        setPinPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      });

      mapRef.current = map;
      markerRef.current = marker;
      setPinPosition(center);
      setMapLoading(false);
    };

    loadGoogleMapsScript()
      .then(initMap)
      .catch(() => {
        if (!cancelled) {
          toast.error('Could not load the map — please try again');
          setMapLoading(false);
        }
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [step]);

  if (step === 'ask') {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3" data-testid="address-location-ask">
        <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
          <MapPin size={18} className="text-sspice" /> Are you at this address right now?
        </p>
        <p className="text-xs text-gray-500">
          If so, we'll double-check it against your location — takes one tap.
        </p>
        <div className="flex gap-2">
          <button
            data-testid="address-location-yes-btn"
            onClick={handleYes}
            className="flex-1 py-2.5 bg-sspice text-white text-sm font-semibold rounded-full"
          >
            Yes, I'm here
          </button>
          <button
            data-testid="address-location-no-btn"
            onClick={handleNo}
            className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-full"
          >
            No, mark on map
          </button>
        </div>
        {onSkip && (
          <button
            data-testid="address-location-skip-btn"
            onClick={onSkip}
            className="w-full text-xs text-gray-400 underline"
          >
            Skip for now
          </button>
        )}
      </div>
    );
  }

  if (step === 'checking') {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center" data-testid="address-location-checking">
        <NavigationArrow size={24} className="text-sspice mx-auto mb-2 animate-pulse" />
        <p className="text-sm text-gray-600">Checking your location…</p>
      </div>
    );
  }

  // step === 'map'
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="address-location-map">
      <div className="p-3 border-b border-gray-100">
        <p className="text-sm font-medium text-gray-800">Drag the pin to the exact delivery spot</p>
        <p className="text-xs text-gray-500 mt-0.5">Tap anywhere on the map, or drag the pin directly.</p>
      </div>
      <div className="relative">
        {mapLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
            <p className="text-xs text-gray-400">Loading map…</p>
          </div>
        )}
        <div ref={mapContainerRef} style={{ height: '260px', width: '100%' }} data-testid="google-map-container" />
      </div>
      <div className="p-3 space-y-2">
        <button
          data-testid="address-location-confirm-btn"
          onClick={() => onConfirm(pinPosition.lat, pinPosition.lng)}
          disabled={mapLoading}
          className="w-full py-2.5 bg-sspice text-white text-sm font-semibold rounded-full flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <CheckCircle size={18} weight="bold" /> Confirm This Location
        </button>
        {onSkip && (
          <button
            data-testid="address-location-skip-btn"
            onClick={onSkip}
            className="w-full text-xs text-gray-400 underline"
          >
            Skip for now
          </button>
        )}
      </div>
    </div>
  );
}
